import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import Draggable from 'react-draggable'
import { Resizable } from 're-resizable'
import {
  CloseOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
} from '@ant-design/icons'
import { selectProfile } from '@store/profile.reducer'
import { closeMeetingIframe } from '@store/meetingIframeSlice'
import { getCorrectFilePath } from '@/helpers/new/getCorrectFilePath'
import s from './index.module.css'

export const meetingIframeProviders = {
  teleBoss: 'teleBoss',
  impulseVks: 'impulseVks',
}

export const isTeleBossMeetingUrl = (value) => {
  if (!value || typeof value !== 'string') return false

  try {
    const url = new URL(value)

    return (
      url.protocol === 'https:' &&
      url.hostname === 'a.teleboss.ru' &&
      url.pathname.startsWith('/room/')
    )
  } catch {
    return false
  }
}

export const isImpulseVksMeetingUrl = (value) => {
  if (!value || typeof value !== 'string') return false

  try {
    const url = new URL(value)

    return (
      url.protocol === 'https:' &&
      ['vks2.impulsschool.online', 'vks.impulsschool.online'].includes(url.hostname)
    )
  } catch {
    return false
  }
}

export const getMeetingIframeProvider = ({
  meetingUrl,
  isTeleBossEnabled,
  isImpulseVksEnabled,
}) => {
  if (isTeleBossEnabled && isTeleBossMeetingUrl(meetingUrl)) {
    return meetingIframeProviders.teleBoss
  }

  if (isImpulseVksEnabled && isImpulseVksMeetingUrl(meetingUrl)) {
    return meetingIframeProviders.impulseVks
  }

  return ''
}

const getMeetingIframeUrl = ({ meetingUrl, participantName, provider }) => {
  if (
    provider === meetingIframeProviders.teleBoss &&
    isTeleBossMeetingUrl(meetingUrl)
  ) {
    const url = new URL(meetingUrl)
    const name = participantName?.trim()

    if (name) {
      url.searchParams.set('participantName', name)
    }

    return url.toString()
  }

  if (
    provider === meetingIframeProviders.impulseVks &&
    isImpulseVksMeetingUrl(meetingUrl)
  ) {
    return meetingUrl
  }

  return ''
}

const getDefaultMinimizedSize = () => ({
  width: Math.min(420, window.innerWidth - 24),
  height: Math.min(window.innerWidth <= 992 ? 220 : 280, window.innerHeight - 24),
})

const getInitialPosition = ({ width, height } = getDefaultMinimizedSize()) => {
  const isDesktop = window.innerWidth > 992
  const rightOffset = isDesktop ? 250 : 16
  const bottomOffset = isDesktop ? 20 : 16

  return {
    x: Math.max(12, window.innerWidth - width - rightOffset),
    y: Math.max(12, window.innerHeight - height - bottomOffset),
  }
}

const resizeDirections = {
  top: true,
  right: true,
  bottom: true,
  left: true,
  topRight: true,
  bottomRight: true,
  bottomLeft: true,
  topLeft: true,
}

const resizeHandleStyles = {
  top: { top: 0 },
  right: { right: 0 },
  bottom: { bottom: 0 },
  left: { left: 0 },
  topRight: { top: 0, right: 0 },
  bottomRight: { right: 0, bottom: 0 },
  bottomLeft: { bottom: 0, left: 0 },
  topLeft: { top: 0, left: 0 },
}

const MeetingIframe = () => {
  const dispatch = useDispatch()
  const profile = useSelector(selectProfile)
  const parentSelectedChild = useSelector((state) => state.parentSelectedChild)
  const meetingIframe = useSelector((state) => state.meetingIframe)
  const platformName = useSelector((state) => state.settings?.app_name)
  const mainLogo = useSelector((state) => state.settings?.mainLogo)
  const [isExpanded, setIsExpanded] = useState(true)
  const [position, setPosition] = useState(getInitialPosition)
  const [minimizedSize, setMinimizedSize] = useState(getDefaultMinimizedSize)
  const windowRef = useRef(null)
  const resizeStartPositionRef = useRef(position)
  const meetingUrl = meetingIframe?.meetingUrl
  const provider = meetingIframe?.provider
  const participantName =
    profile?.role === 'parent'
      ? parentSelectedChild?.data?.name
      : profile?.name

  const iframeUrl = useMemo(
    () => getMeetingIframeUrl({ meetingUrl, participantName, provider }),
    [meetingUrl, participantName, provider],
  )
  const fallbackTitle =
    provider === meetingIframeProviders.teleBoss
      ? 'Урок TeleBoss'
      : 'Урок Импульс-ВКС'
  const title = platformName || process.env.REACT_APP_PROJECT_NAME || fallbackTitle
  const logo = getCorrectFilePath(mainLogo)

  useEffect(() => {
    if (!meetingUrl) return

    const defaultSize = getDefaultMinimizedSize()
    setIsExpanded(true)
    setMinimizedSize(defaultSize)
    setPosition(getInitialPosition(defaultSize))
  }, [meetingUrl, provider])

  useEffect(() => {
    if (!iframeUrl) return undefined

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = isExpanded ? 'hidden' : previousOverflow

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [iframeUrl, isExpanded])

  useEffect(() => {
    if (!iframeUrl || isExpanded) return undefined

    const handleResize = () => setMinimizedSize((currentSize) => {
      const nextSize = {
        width: Math.min(currentSize.width, window.innerWidth - 24),
        height: Math.min(currentSize.height, window.innerHeight - 24),
      }

      setPosition((currentPosition) => ({
        x: Math.min(Math.max(0, currentPosition.x), window.innerWidth - nextSize.width),
        y: Math.min(Math.max(0, currentPosition.y), window.innerHeight - nextSize.height),
      }))

      return nextSize
    })

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [iframeUrl, isExpanded])

  if (!iframeUrl) return null

  return createPortal(
    <div className={s.wrapper}>
      <Draggable
        bounds="parent"
        disabled={isExpanded}
        handle={`.${s.dragHandle}`}
        cancel={`.${s.actions}`}
        nodeRef={windowRef}
        position={isExpanded ? { x: 0, y: 0 } : position}
        onStop={(_, data) => setPosition({ x: data.x, y: data.y })}
      >
        <div
          ref={windowRef}
          className={`${s.window} ${isExpanded ? s.expanded : s.minimized}`}
          style={isExpanded ? undefined : minimizedSize}
        >
          <Resizable
            size={isExpanded ? { width: '100%', height: '100%' } : minimizedSize}
            minWidth={Math.min(320, window.innerWidth - 24)}
            minHeight={Math.min(200, window.innerHeight - 24)}
            maxWidth={isExpanded ? undefined : window.innerWidth}
            maxHeight={isExpanded ? undefined : window.innerHeight}
            bounds="window"
            boundsByDirection
            enable={isExpanded ? false : resizeDirections}
            handleStyles={resizeHandleStyles}
            handleClasses={{
              top: s.resizeEdge,
              right: s.resizeEdge,
              bottom: s.resizeEdge,
              left: s.resizeEdge,
              topRight: s.resizeEdge,
              bottomRight: `${s.resizeEdge} ${s.resizeHandle}`,
              bottomLeft: s.resizeEdge,
              topLeft: s.resizeEdge,
            }}
            onResizeStart={() => {
              resizeStartPositionRef.current = position
            }}
            onResize={(_, direction, element, delta) => {
              const normalizedDirection = direction.toLowerCase()

              setMinimizedSize({
                width: element.offsetWidth,
                height: element.offsetHeight,
              })
              setPosition({
                x: normalizedDirection.includes('left')
                  ? Math.max(0, resizeStartPositionRef.current.x - delta.width)
                  : resizeStartPositionRef.current.x,
                y: normalizedDirection.includes('top')
                  ? Math.max(0, resizeStartPositionRef.current.y - delta.height)
                  : resizeStartPositionRef.current.y,
              })
            }}
            className={s.resizable}
          >
            <div className={`${s.titleBar} ${s.dragHandle}`}>
              <div className={s.titleContent}>
                {logo && <img src={logo} alt="" className={s.logo} />}
                <span className={s.title}>{title}</span>
              </div>
              <div className={s.actions}>
                <button
                  type="button"
                  className={s.windowButton}
                  onClick={() => {
                    if (isExpanded) setPosition(getInitialPosition(minimizedSize))
                    setIsExpanded((value) => !value)
                  }}
                  aria-label={isExpanded ? 'Свернуть окно' : 'Развернуть окно'}
                  title={isExpanded ? 'Свернуть' : 'Развернуть'}
                >
                  {isExpanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                </button>
                <button
                  type="button"
                  className={`${s.windowButton} ${s.closeButton}`}
                  onClick={() => {
                    setIsExpanded(true)
                    dispatch(closeMeetingIframe())
                  }}
                  aria-label="Закрыть встречу"
                  title="Закрыть"
                >
                  <CloseOutlined />
                </button>
              </div>
            </div>
            <iframe
              src={iframeUrl}
              title={title}
              className={s.iframe}
              allow="camera; microphone; fullscreen; accelerometer; autoplay; clipboard-write; encrypted-media; display-capture; gyroscope; picture-in-picture; screen-wake-lock; compute-pressure"
              allowFullScreen
              loading="lazy"
              referrerPolicy="origin"
            />
          </Resizable>
        </div>
      </Draggable>
    </div>,
    document.body,
  )
}

export default MeetingIframe
